import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/system';
import { InlineLegendItem } from './item/inline-legend-item';
import { TabEnum } from '../../../../../../models/enums/tab-enum';
import { AreaChartMobxDto } from '../../../../../../models/mobx/dtos/statistic/area-chart-mobx-dto';
import { Dropdown } from '../../../../dropdown/dropdown';
import { DropdownMenu } from '../../../../dropdown/menu/dropdown-menu';
import { IconCompare } from '../../../../icons/compare';
import { IconArrowDown } from '../../../../icons/arrow-down';

export interface InlineLegendProps {
    data: AreaChartMobxDto[];
    tab: TabEnum;
    onChange: (id: string) => void;
    filter: Map<string, boolean>;
    controls?: React.ReactNode;
    labels?: Record<string, string>;
    tooltips?: Record<string, React.ReactNode>;
}

export const InlineLegend: React.FC<InlineLegendProps> = ({
    data,
    onChange,
    filter,
    controls,
    labels,
    tooltips,
}: InlineLegendProps) => {
    const { t } = useTranslation();

    const isMobile = useMediaQuery('(max-width:1023px)');
    const activeItemsCount = data.filter((item) => filter.get(item.id)).length;

    const LegendMenu = () => {
        return (
            <DropdownMenu>
                {data.map((item, index) => (
                    <InlineLegendItem
                        key={index}
                        label={item.id}
                        color={item.color}
                        chartDataType={item.chartDataType}
                        onChange={() => onChange(item.id)}
                        checked={filter.get(item.id)}
                        displayLabel={labels?.[item.id]}
                        tooltip={tooltips?.[item.id]}
                        disabled={filter.get(item.id) && activeItemsCount <= 1}
                    />
                ))}
            </DropdownMenu>
        );
    };

    return (
        <div className="relative inline-flex max-w-full flex-col items-start gap-y-[0.8rem] lg:flex-row lg:items-center lg:justify-end lg:gap-[1.6rem] vl:-top-[2rem] vl:float-right">
            {controls}
            <div className="hidden max-w-full flex-wrap items-center justify-end gap-[0.8rem] lg:flex">
                {data.map((item, index) => (
                    <InlineLegendItem
                        key={index}
                        label={item.id}
                        color={item.color}
                        chartDataType={item.chartDataType}
                        onChange={() => onChange(item.id)}
                        checked={filter.get(item.id)}
                        displayLabel={labels?.[item.id]}
                        tooltip={tooltips?.[item.id]}
                        disabled={filter.get(item.id) && activeItemsCount <= 1}
                        variant="inline"
                    />
                ))}
            </div>
            <div className="inline-flex min-h-[3.2rem] items-center lg:hidden">
                <Dropdown content={<LegendMenu />} position={isMobile ? 'bottom-left' : 'bottom-right'}>
                    <div className="relative inline-flex cursor-pointer items-center gap-xsmall font-medium text-brand-secondary hover:opacity-80">
                        <IconCompare /> {t('pages.app.dashboard.compare')}{' '}
                        <span className="[&_i]:h-[1rem] [&_i]:w-[1rem] [&_i]:text-text-muted">
                            <IconArrowDown />
                        </span>
                    </div>
                </Dropdown>
            </div>
        </div>
    );
};
